/**
 * DI-3-S61 §2.2 — the History modal's ONE read: intake/v1 1.44.0
 * `getIntakeHistory` (`GET /data-acquisitions/{id}/history`).
 *
 * The cross-namespace pattern of `workflowMutations.ts:useSendBackToPilotMutation`
 * (`reopenIntake`): `useApi()` is base-pinned to `/api/lyse/v1`, so the request
 * carries a per-request `baseURL` from `getIntakeBaseUrl()`; the shared axios
 * instance adds the bearer + X-Tenant-ID. Read-only (AC-5).
 *
 * `staleTime` 60 s; `refetchOnMount: "always"` — the panel mounts when the
 * History section opens, so every open reads afresh.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { getIntakeBaseUrl } from "@aeriosiq/auth-client";
import type { IntakeHistory } from "./historyFormat.js";

export const intakeHistoryKey = (dataAcquisitionId: string) =>
  ["intake", "history", dataAcquisitionId] as const;

export const INTAKE_HISTORY_STALE_MS = 60_000;

export function useIntakeHistoryQuery(
  dataAcquisitionId: string,
): UseQueryResult<IntakeHistory, Error & { status?: number }> {
  const api = useApi();
  return useQuery<IntakeHistory, Error & { status?: number }>({
    queryKey: intakeHistoryKey(dataAcquisitionId),
    enabled: dataAcquisitionId.length > 0,
    staleTime: INTAKE_HISTORY_STALE_MS,
    refetchOnMount: "always",
    queryFn: async () => {
      const response = await api.get<IntakeHistory>(
        `/data-acquisitions/${encodeURIComponent(dataAcquisitionId)}/history`,
        { baseURL: getIntakeBaseUrl() },
      );
      return response.data;
    },
  });
}

/** The status of a failed read — the shared client wraps the AxiosError (`AeroApiError.cause`). */
export function errorStatus(err: unknown): number | null {
  const e = err as {
    status?: number;
    cause?: { response?: { status?: number } };
  } | null;
  return e?.status ?? e?.cause?.response?.status ?? null;
}
